// naumu-face.ts — Naumu logo face state machine.
// Two parametric shapes: brow = circular-arc stroke through 3 points (round caps),
// eye = rounded rect. All keyframes decoded from the Figma prototype (12x12 grid).
// Live params follow keyframe targets via critically-damped springs, so any
// state->state transition is smooth from wherever the face currently is.
//
// Framework-agnostic and dependency-free; `NaumuFaceView` is the React binding.
// Usage:  const face = new NaumuFace(el, { size: 200, color: '#000' });
//         face.setState('thinking'); // 'idle' | 'attentive' | 'thinking' | 'surprised' | 'wink'
//         face.destroy();

export const STATE_NAMES = ['static', 'idle', 'attentive', 'thinking', 'surprised', 'wink'] as const;
export const SURPRISED_MS = 1400; // total length of the transient surprised state
export const WINK_MS = 1080; // total length of the transient wink state

export type FaceState = (typeof STATE_NAMES)[number];

export interface NaumuFaceOptions {
	/** Rendered svg width/height. Any CSS length works ('100%' lets the container size it). */
	size?: number | string;
	color?: string;
	speed?: number;
	/** Pose to start in. Seeded straight into the springs, so it does not visibly settle in from `idle` on mount. */
	state?: FaceState;
	onChange?: (state: FaceState) => void;
}

type ResolvedOptions = Required<Omit<NaumuFaceOptions, 'onChange'>> & Pick<NaumuFaceOptions, 'onChange'>;

type Matrix = [number, number, number, number];

interface ShapeDef {
	pts: number[][];
	w: number;
	nat: [number, number];
}

interface BrowGeom {
	p: number[][];
	w: number;
}

interface EyeGeom {
	x: number;
	y: number;
	w: number;
	h: number;
	rot: number;
}

interface HeadGeom {
	rot: number;
	x: number;
	y: number;
	sq: number;
}

interface Frame {
	b: BrowGeom;
	e: EyeGeom;
	hold: number;
	jitter: number;
	snap: number;
	h: HeadGeom;
}

interface StateDef {
	frames: Frame[];
	still?: boolean;
	then?: FaceState;
}

const R: Matrix = [-0.781, 0.624, 0.624, 0.781]; // shared figma rotation/reflection matrix
const ID: Matrix = [1, 0, 0, 1];

// Local centerlines (start, mid, end on the arc) + stroke width, in each vector's
// natural box. Derived from the outline paths in the .fig / repo SVGs.
const SHAPES: Record<string, ShapeDef> = {
	hookIdle: { pts: [[2, 2], [6.405, 3.595], [8, 8]], w: 4, nat: [10, 10] },
	hookB: { pts: [[1.9, 7.6], [3.415, 3.415], [7.6, 1.9]], w: 3.8, nat: [9.5, 9.5] },
	browSmall: { pts: [[0, 6], [2.26, 2.40], [6, 0]], w: 4, nat: [6, 6] },
	swoosh: { pts: [[0, 0.017], [4.37, 1.64], [5.983, 6]], w: 4, nat: [6, 6] },
	flatWave: { pts: [[0, 2.068], [4.33, 0.02], [8.433, 3.011]], w: 4, nat: [8.433, 3.011] },
	wave22: { pts: [[0, 1.444], [4.28, 0.02], [8.498, 2.444]], w: 4, nat: [8.498, 2.444] },
	v6brow: { pts: [[0, 4.706], [3.75, 0.96], [7.338, 0.272]], w: 4, nat: [7.338, 4.706] },
};

function brow(name: string, m: Matrix, tx: number, ty: number, bw?: number, bh?: number): BrowGeom {
	const s = SHAPES[name];
	const sx = bw ? bw / s.nat[0] : 1, sy = bh ? bh / s.nat[1] : 1;
	let pts = s.pts.map(([x, y]) => [m[0] * sx * x + m[2] * sy * y + tx, m[1] * sx * x + m[3] * sy * y + ty]);
	if (pts[0][0] > pts[2][0]) pts = [pts[2], pts[1], pts[0]]; // normalize left->right
	return { p: pts, w: s.w * Math.sqrt(Math.abs(sx * sy)) };
}
const eye = (x: number, y: number, w: number, h: number, rot = 0): EyeGeom => ({ x, y, w, h, rot });
// whole-head channel: tilt (deg), offset, and volume-preserving squash/stretch (>1 = tall)
const head = (rot = 0, x = 0, y = 0, sq = 1): HeadGeom => ({ rot, x, y, sq });
const NEUTRAL_HEAD = head();
const F = (b: BrowGeom, e: EyeGeom, hold: number, jitter = 0, snap = 1, h: HeadGeom = NEUTRAL_HEAD): Frame =>
	({ b, e, hold, jitter, snap, h });

// State timelines. Each state cycles its frames; springs turn the steps into motion.
// `still` freezes the breathing drift; `then` auto-transitions after the last frame.
const STATES: Record<FaceState, StateDef> = {
	static: {
		still: true, frames: [
			F(brow('hookIdle', ID, 1, 1), eye(1, 7, 4, 4), 1e9),
		]
	},
	idle: {
		frames: [
			F(brow('swoosh', ID, 3, 3), eye(1, 7, 4, 4), 1600, 900),
			F(brow('swoosh', ID, 3, 3), eye(0, 7, 4, 4), 700),
			F(brow('swoosh', ID, 3.148, 2.87), eye(1.148, 6.87, 4, 4), 900, 500),
			F(brow('swoosh', ID, 3, 3), eye(1, 10, 4, 1), 60, 0, 3.5),  // blink: shut to bottom edge, open lazily
			F(brow('swoosh', ID, 3, 3), eye(1, 7, 4, 4), 1400, 800),
			F(brow('swoosh', ID, 3, 3), eye(1, 8, 4, 2), 850),          // sleepy squint
		]
	},
	attentive: {
		frames: [
			F(brow('hookB', R, 6.961, -1.649), eye(4, 7.244, 4, 4), 2600, 1600),
			F(brow('hookB', R, 6.961, -1.649), eye(4, 10.244, 4, 1), 60, 0, 3.5), // blink (bottom edge pinned)
		]
	},
	thinking: {
		frames: [
			F(brow('browSmall', R, 6.688, 2), eye(6, 9.563, 4, 2), 1300, 400),
			F(brow('browSmall', R, 6.333, 2.283, 5.546, 6.677), eye(7, 10, 4, 2, 10.9), 1050),
			F(brow('browSmall', R, 6.721, 2.406), eye(2, 9.563, 4, 2), 1150),
			F(brow('browSmall', R, 6.721, 2.406), eye(1, 9, 4, 2), 900),
			F(brow('swoosh', ID, 4.149, 2.427), eye(2.149, 7.427, 4, 2), 1300),
			F(brow('v6brow', R, 7.563, 0.952), eye(7.36, 7.34, 4, 4.19, 10.9), 1300),
			F(brow('browSmall', R, 6.688, 2), eye(6, 9.563, 4, 2), 1050),
		]
	},
	surprised: {
		then: 'attentive', frames: [
			F(brow('flatWave', ID, 2, 0.958), eye(4, 6, 4, 6), 700, 0, 1.6), // brow raised + eye popped from frame one
			F(brow('flatWave', ID, 2, 0.958), eye(4, 6.281, 4, 6), 700),
		]
	},
	// One-off wink: wind up, close the lid with a head tilt + nod, pop open, settle -> idle.
	wink: {
		then: 'idle', frames: [
			F(brow('hookB', R, 6.961, -2.1), eye(4, 7.1, 4, 4.25), 170, 0, 1.5, head(-5.5, -0.12, -0.22, 1.03)),  // anticipation: chin up
			F(brow('hookB', R, 6.961, -1.15), eye(4, 9.9, 4, 1.5), 110, 0, 1.8, head(8, 0.18, 0.48, 0.955)),      // the hit: lid down, head nods
			F(brow('hookB', R, 6.961, -1.3), eye(4, 9.85, 4, 1.5), 220, 0, 1.25, head(6, 0.12, 0.24, 0.99)),      // hold the wink, secondary bob
			F(brow('hookB', R, 6.961, -2.35), eye(4, 7, 4, 4.5), 270, 0, 1.5, head(-3, -0.06, -0.16, 1.02)),      // pop open on the rebound
			F(brow('hookB', R, 6.961, -1.649), eye(4, 7.244, 4, 4), 310, 0, 1.1, head(1.5, 0.03, 0.05, 0.997)),   // settle
		]
	},
};

// param vector: [b0x,b0y,bmx,bmy,b2x,b2y,bw, ecx,ecy,ew,eh,erot, hrot,hx,hy,hsq]
const OMEGA = [12, 12, 12, 12, 12, 12, 12, 16, 16, 13, 13, 14, 13, 14, 13, 15];
function flatten(f: Frame, out: Float32Array): Float32Array {
	const p = f.b.p, e = f.e, h = f.h;
	out[0] = p[0][0]; out[1] = p[0][1]; out[2] = p[1][0]; out[3] = p[1][1];
	out[4] = p[2][0]; out[5] = p[2][1]; out[6] = f.b.w;
	out[7] = e.x + e.w / 2; out[8] = e.y + e.h / 2; out[9] = e.w; out[10] = e.h; out[11] = e.rot;
	out[12] = h.rot; out[13] = h.x; out[14] = h.y; out[15] = h.sq;
	return out;
}
const N = 16;
// Per-channel damping floor. Eye size and head channels must not overshoot much:
// a negative eye height renders as a hairline scratch, and a wild head tilt reads as
// a glitch rather than a nod. Everything else keeps the loose, bouncy feel.
const ZETA_FLOOR = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0.9, 0.9, 0, 0.62, 0.5, 0.5, 0.75];
const EYE_MIN = 0.55; // clamp the spring state, not just the render output
const SETTLE_EPS = 2e-3; // grid units: sub-pixel at any size the face ships in
const MAX_TIMEOUT_MS = 2 ** 31 - 1; // longer delays overflow setTimeout and fire at once
const TAU = Math.PI * 2;
function arcPath(ax: number, ay: number, bx: number, by: number, cx: number, cy: number): string {
	const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
	if (Math.abs(d) < 1e-4) return `M ${ax} ${ay} L ${cx} ${cy}`;
	const a2 = ax * ax + ay * ay, b2 = bx * bx + by * by, c2 = cx * cx + cy * cy;
	const ux = (a2 * (by - cy) + b2 * (cy - ay) + c2 * (ay - by)) / d;
	const uy = (a2 * (cx - bx) + b2 * (ax - cx) + c2 * (bx - ax)) / d;
	const r = Math.hypot(ax - ux, ay - uy);
	const a0 = Math.atan2(ay - uy, ax - ux);
	const da1 = ((Math.atan2(by - uy, bx - ux) - a0) % TAU + TAU) % TAU;
	const da2 = ((Math.atan2(cy - uy, cx - ux) - a0) % TAU + TAU) % TAU;
	const sweep = da1 < da2 ? 1 : 0;
	const span = sweep ? da2 : TAU - da2;
	const large = span > Math.PI ? 1 : 0;
	return `M ${ax} ${ay} A ${r} ${r} 0 ${large} ${sweep} ${cx} ${cy}`;
}

const SVGNS = 'http://www.w3.org/2000/svg';
export class NaumuFace {
	opts: ResolvedOptions;
	el: SVGSVGElement;
	cur: Float32Array;
	vel: Float32Array;
	target: Float32Array;
	private _wrap: HTMLDivElement;
	private _g: SVGGElement;
	private _brow: SVGPathElement;
	private _eye: SVGRectElement;
	private _state: FaceState;
	private _fi: number;
	private _snap: number;
	private _kickT: number;
	private _timer: number;
	private _last: number;
	private _raf: number;
	/** Hold timer while the springs are at rest; the loop only runs while something moves. */
	private _sleep: ReturnType<typeof setTimeout> | null = null;
	private _paused = false;

	constructor(container: HTMLElement, opts: NaumuFaceOptions = {}) {
		this.opts = Object.assign({ size: 96, color: '#000', speed: 1, state: 'idle' as FaceState }, opts);
		const svg = document.createElementNS(SVGNS, 'svg');
		svg.setAttribute('viewBox', '-1.5 -1.5 15 15');
		svg.setAttribute('width', String(this.opts.size));
		svg.setAttribute('height', String(this.opts.size));
		svg.style.overflow = 'visible';
		this._g = document.createElementNS(SVGNS, 'g');
		this._brow = document.createElementNS(SVGNS, 'path');
		this._brow.setAttribute('fill', 'none');
		this._brow.setAttribute('stroke', this.opts.color);
		this._brow.setAttribute('stroke-linecap', 'round');
		this._eye = document.createElementNS(SVGNS, 'rect');
		this._eye.setAttribute('fill', this.opts.color);
		this._g.appendChild(this._brow); this._g.appendChild(this._eye);
		svg.appendChild(this._g);
		// The breathing drift animates this wrapper, not the svg: Chromium runs CSS
		// transform animations on the compositor for HTML boxes but repaints SVG roots.
		this._wrap = document.createElement('div');
		this._wrap.style.display = 'inline-block';
		this._wrap.style.lineHeight = '0';
		// Attribute writes relayout the svg every tick; containment keeps that (and the
		// resulting paint invalidation) inside this box instead of the whole page.
		this._wrap.style.contain = 'layout';
		const cssSize = typeof this.opts.size === 'number' ? `${this.opts.size}px` : String(this.opts.size);
		this._wrap.style.width = cssSize;
		this._wrap.style.height = cssSize;
		this._wrap.appendChild(svg);
		container.appendChild(this._wrap);
		this.el = svg;

		this.cur = new Float32Array(N); this.vel = new Float32Array(N);
		this.target = new Float32Array(N);
		const initial = STATES[this.opts.state] ? this.opts.state : 'idle';
		this._state = initial; this._fi = 0; this._snap = 1; this._kickT = 0;
		const first = STATES[initial].frames[0];
		flatten(first, this.cur); this.target.set(this.cur);
		this._timer = first.hold;
		this._last = performance.now();
		this._syncAlive();
		this._render();
		this._raf = requestAnimationFrame(this._tick);
	}
	get state(): FaceState { return this._state; }
	setState(name: FaceState) {
		if (!STATES[name]) return;
		if (name === this._state && !STATES[name].then) return; // transient states may restart
		this._state = name; this._fi = 0;
		const f = STATES[name].frames[0];
		flatten(f, this.target);
		this._timer = f.hold + Math.random() * (f.jitter || 0);
		this._snap = f.snap || 1;
		this._kickT = 0.65; // punchy underdamped kick on every state change
		this._syncAlive();
		this._wake();
		if (this.opts.onChange) this.opts.onChange(name);
	}
	setSpeed(s: number) { this.opts.speed = s || 1; }
	setColor(c: string) { this._brow.setAttribute('stroke', c); this._eye.setAttribute('fill', c); }
	setSize(px: number | string) { this.el.setAttribute('width', String(px)); this.el.setAttribute('height', String(px)); }
	/** Stop burning a rAF while offscreen. Pose is preserved; `resume` picks it back up. */
	pause() {
		this._paused = true;
		this._stop();
	}
	resume() {
		if (!this._paused) return;
		this._paused = false;
		this._wake();
	}
	destroy() { this.pause(); this._wrap.remove(); }

	/** The breathing drift is a CSS animation on the svg (`naumu-face-alive`), so a resting face owns no frames. */
	private _syncAlive() {
		this._wrap.classList.toggle('naumu-face-alive', !STATES[this._state].still);
	}
	private _stop() {
		if (this._raf) cancelAnimationFrame(this._raf);
		this._raf = 0;
		if (this._sleep !== null) clearTimeout(this._sleep);
		this._sleep = null;
	}
	/** Start ticking unless already running or paused. Discards the idle span so springs don't jump. */
	private _wake() {
		if (this._paused || this._raf) return;
		if (this._sleep !== null) clearTimeout(this._sleep);
		this._sleep = null;
		this._last = performance.now();
		this._raf = requestAnimationFrame(this._tick);
	}
	private _settled(): boolean {
		for (let i = 0; i < N; i++) {
			if (Math.abs(this.target[i] - this.cur[i]) > SETTLE_EPS || Math.abs(this.vel[i]) > SETTLE_EPS) return false;
		}
		return true;
	}
	/** Park the loop until the current pose's hold runs out (or forever for a permanent hold). */
	private _sleepFor(ms: number) {
		this._raf = 0;
		if (ms > MAX_TIMEOUT_MS) return; // `static` holds 1e9 ms: only setState wakes it
		this._sleep = setTimeout(() => {
			this._sleep = null;
			this._timer = 0; // the hold is over: the next tick advances to the following pose
			this._wake();
		}, Math.max(0, ms));
	}

	_tick = (now: number) => {
		const dt = Math.min(0.05, (now - this._last) / 1000); this._last = now;
		this._timer -= dt * 1000 * this.opts.speed;
		if (this._timer <= 0) {
			const def = STATES[this._state];
			if (def.then && this._fi === def.frames.length - 1) {
				this.setState(def.then);
			} else {
				this._fi = (this._fi + 1) % def.frames.length;
				const f = def.frames[this._fi];
				flatten(f, this.target);
				this._timer = f.hold + Math.random() * (f.jitter || 0);
				this._snap = f.snap || 1;
			}
		}
		this._kickT = Math.max(0, this._kickT - dt);
		const kick = this._kickT > 0;
		const zeta = kick ? 0.32 : 0.55; // underdamped everywhere; extra wild mid-transition
		const wMul = (kick ? 1.35 : 1) * this._snap * Math.sqrt(this.opts.speed);
		// Fixed-step substepping: stiff springs (fast blinks, wink slam) stay stable even
		// when a frame is long. Explicit Euler needs w*h << 2; h is capped at 1/240s.
		const steps = Math.min(24, Math.max(1, Math.ceil(dt * 240 * Math.max(1, wMul))));
		const h = dt / steps;
		for (let s = 0; s < steps; s++) {
			for (let i = 0; i < N; i++) {
				const w = OMEGA[i] * wMul;
				const z = Math.max(zeta, ZETA_FLOOR[i]);
				this.vel[i] += (w * w * (this.target[i] - this.cur[i]) - 2 * z * w * this.vel[i]) * h;
				this.cur[i] += this.vel[i] * h;
			}
			// eye can squash thin but never through zero (that renders as a stray hairline)
			for (let i = 9; i <= 10; i++) {
				if (this.cur[i] < EYE_MIN) { this.cur[i] = EYE_MIN; if (this.vel[i] < 0) this.vel[i] = 0; }
			}
		}
		// whole-head motion (tilt / nod / squash); the breathing drift is CSS (`naumu-face-alive`)
		const q = this.cur;
		const sq = Math.min(1.4, Math.max(0.7, q[15] || 1));
		this._g.setAttribute('transform',
			`translate(${6 + q[13]} ${6 + q[14]}) rotate(${q[12]}) scale(${1 / sq} ${sq}) translate(-6 -6)`);
		this._render();
		// At rest inside a hold there is nothing to draw: park until the hold ends.
		if (this._kickT <= 0 && this._settled()) {
			this._sleepFor(this._timer / this.opts.speed);
			return;
		}
		this._raf = requestAnimationFrame(this._tick);
	};
	_render() {
		const q = this.cur;
		this._brow.setAttribute('d', arcPath(q[0], q[1], q[2], q[3], q[4], q[5]));
		this._brow.setAttribute('stroke-width', String(Math.max(0.1, q[6])));
		const w = Math.max(0.2, q[9]), h = Math.max(0.2, q[10]);
		this._eye.setAttribute('x', String(q[7] - w / 2)); this._eye.setAttribute('y', String(q[8] - h / 2));
		this._eye.setAttribute('width', String(w)); this._eye.setAttribute('height', String(h));
		this._eye.setAttribute('rx', String(Math.min(1.5, h / 2, w / 2)));
		this._eye.setAttribute('transform', `rotate(${q[11]} ${q[7]} ${q[8]})`);
	}
}
